<template>
	<template v-if="visible">
		<template v-if="multiAction">
			<anchor-element tag="span" :chain="chain" class="multi-button" :class="[wrapperClazz, animationClasses]">
				<button v-if="buttonLabel" type="button" class="fd-button" :class="clazz" :style="[style, buttonStyle]" v-bind="coreBinding" :disabled="working || dis" @click="doAction()" ref="btn">
					<sym-icon v-if="feedbackIcon" :name="feedbackIcon" class="fd-feedback-icon"></sym-icon>
					<template v-else>
						<sym-icon v-if="hasIcon" :name="button.icon" class="fd-button-icon"></sym-icon>
						{{ buttonLabel }}
					</template>
				</button>
				<button type="button" class="button-drop" :disabled="working || dis" :class="clazz" @click="showActions"><sym-icon name="menu-drop"></sym-icon></button>
			</anchor-element>
			<boat-element tag="nav" :chain="chain" class="menu-panel multi-button-drop">
				<div class="menu-panel-body">
					<template v-for="a in button.more">
						<template v-if="a.action">
							<a v-if="menuHasIcons" href="#" class="menu-item" @click.prevent="doAction(a.action)" v-bind="menuItemBinding(a)">
								<sym-icon v-if="a.icon" :name="a.icon" class="menu-fi"></sym-icon>
								<span v-else class="fi menu-fi"></span>
								<span class="mi mi-label">{{ a.label }}</span>
							</a>
							<a v-else href="#" class="menu-item" @click.prevent="doAction(a.action)" v-bind="menuItemBinding(a)">{{ a.label }}</a>
						</template>
						<template v-else>
							<div v-if="a.label" class="separator-item separator-item-with-title">{{ a.label }}</div>
							<div v-else class="separator-item"></div>
						</template>
					</template>
				</div>
			</boat-element>
		</template>
		<template v-else>
			<button v-if="buttonLabel" type="button" class="fd-button" :class="[clazz, animationClasses]" :style="[style, buttonStyle]" v-bind="coreBinding" :disabled="working || dis" @click="doAction()" ref="btn">
				<sym-icon v-if="feedbackIcon" :name="feedbackIcon" class="fd-feedback-icon"></sym-icon>
				<template v-else>
					<sym-icon v-if="hasIcon" :name="button.icon" class="fd-button-icon"></sym-icon>
					{{ buttonLabel }}
				</template>
			</button>
			<icon-button v-else :name="button.icon" :class="clazz" :style="style" v-bind="coreBinding" :disabled="working || dis" @click="doAction()"></icon-button>
		</template>
	</template>
</template>

<script setup lang="ts">
import SymIcon from "../helpers/SymIcon.vue";
import {Button, ButtonProps} from "@t/fd/fixed/Button";
import usePart, {type PartProps, partProps} from "../Part";
import {computed, ref, nextTick, type CSSProperties} from "vue";
import IconButton from "../helpers/IconButton.vue";
import {ElementMeasure, getElementMeasure} from "@fd/helpers/DomUtils";
import {ERROR, hapticFeedback, SUCCESS} from "@/utils/Haptic.ts";
import {Chain, ChainRule, createChain} from "@/ui/paneltree/PanelTree.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {connectEventBus} from "@fd/Environment.ts";
import {injectExecute} from "@fd/PartInjectKeys.ts";

const uprops = defineProps({...partProps});
const props = uprops as PartProps;
const button = computed<Button>(() => props.part as Button);
const {visible, clazz, style, localData, ro} = usePart(props);

const dis = computed<boolean>(() => {
	return !!button.value.mutating && ro.value;
});
const buttonLabel = computed(() => {
	return button.value.label;
});
const hasIcon = computed(() => {
	return !!button.value.icon;
});
const multiAction = computed<boolean>(() => Array.isArray(button.value.more) && button.value.more.length > 0);
const extraCount = computed<number>(() => Array.isArray(button.value.more) ? button.value.more.length : 0);	// number of extra commands
/* Used to reserve spece and properly vertically align icons and labels*/
const menuHasIcons = computed<boolean>(() => {
	if (extraCount.value === 0) return false;
	for (let act of button.value.more) {
		if (act.icon && act.action) return true;
	}
	return false;
});
const BORDER = 1, PADDING = 8, ITEM_HEIGHT = 26;
const estimatedExtraCommandsPanelHeight = computed<number>(() => {
	return 2*BORDER + 2*PADDING + extraCount.value*ITEM_HEIGHT;
});

const ABOVE:ChainRule = {
	ah: 100, av: 0,
	bh: 100, bv: 100,
	x: 0, y: -1 };
const BELLOW:ChainRule = {
	ah: 100, av: 100,
	bh: 100, bv: 0,
	x: 0, y: 1 };
const chain:Chain = multiAction.value ? createChain({
	rule: (m:ElementMeasure) => {
		const est = estimatedExtraCommandsPanelHeight.value;
		if (est <= m.spaceBottom || m.spaceBottom >= m.spaceTop) {
			return BELLOW;
		} else {
			return ABOVE;
		}
		// if (est <= m.spaceTop || m.spaceTop >= m.spaceBottom) {
		// 	return ABOVE;
		// } else {
		// 	return BELLOW;
		// }
	}
}) : null;

const bus = connectEventBus();
const execute = injectExecute();

const btn = ref<HTMLElement|null>(null);
const btnWidth = ref<number>(0);
const feedbackIcon = ref<string|null>(null);
const working = ref<boolean>(false);	// working feedback = running dot + disabled
const animating = ref<boolean>(false);
const animationName = ref<string|null>(null);
const animationClasses = computed<string[]>(() => {
	const c:string[] = [];
	if (working.value) c.push("working");
	if (animating.value) c.push("fd-feedback");
	if (animationName.value != null) c.push("fd-feedback-"+animationName.value);
	return c;
});
const buttonStyle = computed<CSSProperties>(() => {
	const s:CSSProperties = {};
	if (btnWidth.value !== 0) s.width = btnWidth.value + "px";
	return s;
});
const coreBinding = computed<any>(() => {
	const cb:any = {};
	if (typeof button.value.tabindex === "number") cb.tabindex = button.value.tabindex;
	if (working.value || dis.value) cb.disabled = "";
	if (button.value.title && !dis.value) cb.title = button.value.title;
	return cb;
});
const wrapperClazz = computed(() => {
	const btnClazz = clazz.value;
	let classes:string[];
	if (typeof btnClazz === "string") {
		classes = btnClazz.trim().split(/\s+/);
	} else if (Array.isArray(btnClazz)) {
		classes = btnClazz as string[];
	}
	for (let cls of classes) {
		if (cls === "sbutton" || cls === "fbutton") return "secondary-colors";
	}
	return [];
});


const SUCCESS_ICON = "checkmark-16-heavy";
const FAILURE_ICON = "cross-16-heavy";
const ANIMATION_LEN = 750;	// keep in sync with .fd-feedback

function doFeedbackAnimation(name:"success"|"failure", icon:string) {
	working.value = false;
	let m = null;
	if (btn.value) {
		m = getElementMeasure(btn.value);
		btnWidth.value = m.width;
	}
	animating.value = true;
	animationName.value = name;
	feedbackIcon.value = icon;
	setTimeout(() => {
		animationName.value = null;
	}, ANIMATION_LEN);
	setTimeout(() => {
		feedbackIcon.value = null;
	}, 1.5 * ANIMATION_LEN);
	setTimeout(() => {
		animating.value = false;
		btnWidth.value = 0;
	}, 2 * ANIMATION_LEN);
}

function workingFeedback() {
	working.value = true;
}

function successFeedback() {
	hapticFeedback(SUCCESS);
	doFeedbackAnimation("success", SUCCESS_ICON);
}
function failureFeedback() {
	hapticFeedback(ERROR);
	doFeedbackAnimation("failure", FAILURE_ICON);
}

async function doAction(action?:string, busExecute?: boolean) {
	if (chain) chain.hide();
	if (working.value || dis.value) return;
	const a = action ?? button.value.action;
	let bex:boolean;
	if (typeof busExecute === "boolean") bex = busExecute;
	else {
		if (typeof button.value.busExecute === "boolean") bex = button.value.busExecute;
		else bex = false;
	}

	if (a) {
		if (bex) {
			try {
				working.value = true;
				execute(a, localData.value, button.value);
			} finally {
				working.value = false;
			}
		} else {
			try {
				if (button.value.feedback) workingFeedback();
				const success = await bus.executeCommand(a, localData.value, button.value);
				if (button.value.feedback) {
					if (success) successFeedback();
					else failureFeedback();
				}
			} catch (ex) {
				console.error(ex);
				failureFeedback();
			} finally {
				working.value = false;
			}
		}
	}
}

function showActions() {
	chain.toggle();
}

function menuItemBinding(a:ButtonProps):any {
	const cb:any = {};
	if (a.title) cb.title = a.title;
	return cb;
}
</script>

<style>
.fd-button {
}

.fd-button-icon {
	margin-right: 4px;
}


/* Utility class clickable icon around inputs*/
.fd-active-icon {
	line-height: var(--input-height, 18px);
	--link-decoration: none;
	--link-decoration-hover: none;
	align-self: baseline;
}

.fi.fd-active-icon,
.fd-active-icon > .fi {
	width: 20px;
}

/* While doing feedback */
.fd-feedback {
	transition: all 750ms ease;
}

.fd-feedback-success {
	--bg: var(--green) !important;
	--button-hue: 120;
	--button-sat: 45%;
	--button-lightness: 58%;
}

.fd-feedback-failure {
	--bg: var(--red) !important;
	--button-hue: 6;
	--button-sat: 80%;
	--button-lightness: 58%;
}
.fi.fd-feedback-icon {
	line-height: unset;
}
</style>
